import React, { useEffect } from 'react';

const TemplateMethodPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Template Method Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Template Method Pattern - define algorithm skeleton in base class, let subclasses override specific steps. Learn with data processing and game examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Template Method Pattern - define algorithm skeleton in base class, let subclasses override specific steps. Learn with data processing and game examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'template method pattern, design patterns, behavioral patterns, algorithm skeleton, inheritance, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'template method pattern, design patterns, behavioral patterns, algorithm skeleton, inheritance, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Template Method Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Define algorithm skeleton, let subclasses override steps
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Template Method pattern defines the skeleton of an algorithm in a base class, 
            letting subclasses override specific steps without changing the algorithm's structure. 
            It promotes code reuse and ensures consistent algorithm execution.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You have multiple classes that implement similar algorithms with slight variations. 
            You want to eliminate code duplication while allowing customization of specific steps 
            in the algorithm.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Data Processing Pipeline</h2>
          <div style={styles.codeBlock}>
{`// Abstract template class
abstract class DataProcessor {
    // Template method - defines the algorithm skeleton
    public final void processData() {
        loadData();
        validateData();
        transformData();
        saveData();
        cleanup();
    }
    
    // Abstract methods - must be implemented by subclasses
    protected abstract void loadData();
    protected abstract void transformData();
    protected abstract void saveData();
    
    // Hook methods - can be overridden by subclasses (optional)
    protected void validateData() {
        System.out.println("Performing basic data validation");
    }
    
    protected void cleanup() {
        System.out.println("Cleaning up resources");
    }
}

// Concrete implementations
class CSVDataProcessor extends DataProcessor {
    @Override
    protected void loadData() {
        System.out.println("Loading data from CSV file");
    }
    
    @Override
    protected void transformData() {
        System.out.println("Transforming CSV data to objects");
    }
    
    @Override
    protected void saveData() {
        System.out.println("Saving data to database");
    }
    
    @Override
    protected void validateData() {
        System.out.println("Validating CSV format and required fields");
    }
}

class XMLDataProcessor extends DataProcessor {
    @Override
    protected void loadData() {
        System.out.println("Loading data from XML file");
    }
    
    @Override
    protected void transformData() {
        System.out.println("Parsing XML and transforming to objects");
    }
    
    @Override
    protected void saveData() {
        System.out.println("Saving data to cloud storage");
    }
    
    @Override
    protected void validateData() {
        System.out.println("Validating XML schema and structure");
    }
}

class JSONDataProcessor extends DataProcessor {
    @Override
    protected void loadData() {
        System.out.println("Loading data from JSON API");
    }
    
    @Override
    protected void transformData() {
        System.out.println("Deserializing JSON to objects");
    }
    
    @Override
    protected void saveData() {
        System.out.println("Saving data to NoSQL database");
    }
    
    // Uses default validation - no override needed
}

// Usage
public class TemplateMethodDemo {
    public static void main(String[] args) {
        System.out.println("Processing CSV data:");
        DataProcessor csvProcessor = new CSVDataProcessor();
        csvProcessor.processData();
        
        System.out.println("\\nProcessing XML data:");
        DataProcessor xmlProcessor = new XMLDataProcessor();
        xmlProcessor.processData();
        
        System.out.println("\\nProcessing JSON data:");
        DataProcessor jsonProcessor = new JSONDataProcessor();
        jsonProcessor.processData();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Code Reuse:</strong> Common algorithm structure is reused</li>
            <li style={styles.listItem}><strong>Consistency:</strong> Ensures algorithm steps are executed in correct order</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Subclasses can customize specific steps</li>
            <li style={styles.listItem}><strong>Control:</strong> Parent class controls the overall algorithm flow</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you have multiple classes with similar algorithms</li>
            <li style={styles.listItem}>When you want to eliminate code duplication</li>
            <li style={styles.listItem}>When you need to control algorithm execution order</li>
            <li style={styles.listItem}>When you want to allow customization of specific algorithm steps</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Template Method pattern is excellent for eliminating code duplication while 
            maintaining algorithm consistency. It provides a clean way to define common behavior 
            in a base class while allowing subclasses to customize specific steps.
          </p>
        </section>
      </main>
    </article>
  );
};

export default TemplateMethodPattern;
